import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  channelApi, agentApi,
  type Channel, type Agent, type AuthMode, type ChannelType,
} from '@/lib/api';
import {
  Plus, Trash2, Copy, Check, X, Radio,
  Globe, Lock, UserCheck, Smartphone, Edit2, RefreshCw,
} from 'lucide-react';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { PageGuide } from '@/components/PageGuide';
import { SettingsEmptyState } from '@/components/SettingsEmptyState';
import styles from './_auth.channels.module.css';

export const Route = createFileRoute('/_auth/channels')({
  component: ChannelsPage,
});

// ── Channel Type labels ────────────────────────────────────
const CHANNEL_TYPES: { value: ChannelType; label: string; icon: string }[] = [
  { value: 'WEB_CHAT', label: 'Web Chat', icon: '💬' },
  { value: 'API', label: 'API', icon: '🔌' },
  { value: 'WHATSAPP', label: 'WhatsApp', icon: '📱' },
  { value: 'SMS', label: 'SMS', icon: '✉️' },
  { value: 'EMAIL', label: 'Email', icon: '📧' },
  { value: 'VOICE', label: 'Voice', icon: '🎙️' },
];

// ── Auth Mode cards ────────────────────────────────────────
const AUTH_MODES: { value: AuthMode; label: string; desc: string; Icon: typeof Globe }[] = [
  { value: 'PUBLIC', label: 'Public', desc: 'Anonymous — no auth required', Icon: Globe },
  { value: 'OPTIONAL', label: 'Optional', desc: 'Auth accepted but not required', Icon: UserCheck },
  { value: 'AUTHENTICATED', label: 'Authenticated', desc: 'JWT required — reject if invalid', Icon: Lock },
  { value: 'IDENTITY', label: 'Identity', desc: 'Identity from channel (phone, email)', Icon: Smartphone },
];

export default function ChannelsPage() {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingChannel, setEditingChannel] = useState<Channel | null>(null);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    sourceApp: '',
    type: 'WEB_CHAT' as ChannelType,
    agentId: '',
    authMode: 'PUBLIC' as AuthMode,
    jwksUrl: '',
    jwtIssuer: '',
    jwtAudience: '',
    defaultSessionContext: '',
    allowedOrigins: '',
    rateLimitPerMinute: 60,
  });
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [rotateId, setRotateId] = useState<string | null>(null);
  const [rotating, setRotating] = useState(false);

  const load = () => {
    channelApi.list().then(setChannels).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    agentApi.list().then(setAgents).catch(() => {});
  }, []);

  function handleStartEdit(ch: Channel) {
    setEditingChannel(ch);
    setForm({
      name: ch.name,
      description: ch.description || '',
      sourceApp: ch.sourceApp,
      type: ch.type,
      agentId: ch.agentId || '',
      authMode: ch.authMode,
      jwksUrl: ch.authConfig?.jwksUrl || '',
      jwtIssuer: ch.authConfig?.jwtIssuer || '',
      jwtAudience: ch.authConfig?.jwtAudience || '',
      defaultSessionContext: ch.defaultSessionContext ? JSON.stringify(ch.defaultSessionContext, null, 2) : '',
      allowedOrigins: ch.allowedOrigins ? ch.allowedOrigins.join(', ') : '',
      rateLimitPerMinute: ch.rateLimitPerMinute,
    });
    setShowModal(true);
  }

  function handleCloseModal() {
    setShowModal(false);
    setEditingChannel(null);
    setForm({
      name: '', description: '', sourceApp: '', type: 'WEB_CHAT', agentId: '',
      authMode: 'PUBLIC', jwksUrl: '', jwtIssuer: '', jwtAudience: '',
      defaultSessionContext: '', allowedOrigins: '', rateLimitPerMinute: 60,
    });
  }

  async function handleSave() {
    setSaving(true);
    try {
      const payload: any = {
        name: form.name,
        type: form.type,
        authMode: form.authMode,
        rateLimitPerMinute: form.rateLimitPerMinute,
      };
      if (form.description) payload.description = form.description;
      if (form.sourceApp) payload.sourceApp = form.sourceApp;
      payload.agentId = form.agentId || null;
      if (form.allowedOrigins.trim()) {
        payload.allowedOrigins = form.allowedOrigins.split(',').map(s => s.trim()).filter(Boolean);
      } else {
        payload.allowedOrigins = [];
      }
      // JWT auth config
      if ((form.authMode === 'AUTHENTICATED' || form.authMode === 'OPTIONAL') && form.jwksUrl) {
        payload.authConfig = {
          jwksUrl: form.jwksUrl,
          jwtIssuer: form.jwtIssuer || undefined,
          jwtAudience: form.jwtAudience || undefined,
        };
      } else {
        payload.authConfig = null;
      }
      // Default session context
      if (form.defaultSessionContext.trim()) {
        try {
          payload.defaultSessionContext = JSON.parse(form.defaultSessionContext);
        } catch {
          // ignore invalid JSON — let server validate
        }
      } else {
        payload.defaultSessionContext = null;
      }

      if (editingChannel) {
        await channelApi.update(editingChannel.id, payload);
      } else {
        const result = await channelApi.create(payload);
        setNewKey(result.key || result.channelKey);
      }
      
      handleCloseModal();
      load();
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    await channelApi.delete(id);
    setDeleteId(null);
    load();
  }

  async function handleRotateKey() {
    if (!rotateId) return;
    setRotating(true);
    try {
      const result = await channelApi.rotateKey(rotateId);
      setNewKey(result.channelKey);
      setRotateId(null);
      load();
    } finally {
      setRotating(false);
    }
  }

  function handleCopy() {
    if (newKey) {
      void navigator.clipboard.writeText(newKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  const channelTypeLabel = (type: string) => CHANNEL_TYPES.find(c => c.value === type);
  const authModeInfo = (mode: string) => AUTH_MODES.find(m => m.value === mode);

  return (
    <div className={styles.page}>
      <PageGuide pageKey="channels" />

      <div className={styles.header}>
        <div>
          <h1 className={styles.title}><Radio size={22} /> Channels</h1>
          <p className={styles.subtitle}>
            Inbound connections — web widgets, partner APIs, WhatsApp, voice, and more.
            Each channel gets its own API key and auth configuration.
          </p>
        </div>
        <button id="create-channel" className={styles.createBtn} onClick={() => setShowModal(true)}>
          <Plus size={16} /> New Channel
        </button>
      </div>

      {/* One-time key reveal */}
      {newKey && (
        <div className={styles.keyReveal}>
          <div className={styles.keyRevealHeader}>
            <Check size={18} className={styles.successIcon} />
            <strong>Channel key created — copy it now. It won't be shown again.</strong>
            <button className={styles.dismissBtn} onClick={() => setNewKey(null)}><X size={14} /></button>
          </div>
          <div className={styles.keyDisplay}>
            <code className={styles.keyCode}>{newKey}</code>
            <button id="copy-channel-key" className={styles.copyBtn} onClick={handleCopy}>
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      <div className={styles.table}>
        <div className={styles.theadChannels}>
          <div className={styles.theadCell}>Name</div>
          <div className={styles.theadCell}>Type</div>
          <div className={styles.theadCell}>Auth</div>
          <div className={styles.theadCell}>Agent</div>
          <div className={styles.theadCell}>Key</div>
          <div className={styles.theadCell}>Usage</div>
          <div className={styles.theadCell}>Status</div>
          <div className={styles.theadCell}></div>
        </div>

        {loading && [1, 2].map(i => <div key={i} className={`${styles.rowChannels} ${styles.skeleton}`} />)}

        {!loading && channels.length === 0 && (
          <SettingsEmptyState
            icons={[
              { icon: <Radio size={18} />, color: '#8b5cf6' },
            ]}
            title="No Channels yet"
            description="Create a channel to connect an inbound source — web chat widget, partner API, WhatsApp, or voice. Each channel gets its own key and auth configuration."
            buttonLabel="Create Channel"
            onAction={() => setShowModal(true)}
          />
        )}

        {!loading && channels.map(ch => {
          const ct = channelTypeLabel(ch.type);
          const am = authModeInfo(ch.authMode);
          return (
            <div key={ch.id} className={styles.rowChannels}>
              <div className={styles.cell}>
                <div>
                  <strong>{ch.name}</strong>
                  {ch.description && <div className={styles.cellSub}>{ch.description}</div>}
                  <div className={styles.cellSub}><code className={styles.prefix}>{ch.sourceApp}</code></div>
                </div>
              </div>
              <div className={styles.cell}>
                <span className={styles.channelBadge}>
                  {ct?.icon} {ct?.label || ch.type}
                </span>
              </div>
              <div className={styles.cell}>
                <span className={`${styles.authBadge} ${styles[`auth${ch.authMode}`] || ''}`}>
                  {am?.label || ch.authMode}
                </span>
              </div>
              <div className={styles.cell}>
                {agents.find(a => a.id === ch.agentId)?.name || <span className={styles.cellMuted}>Auto</span>}
              </div>
              <div className={styles.cell}>
                {ch.apiKey ? (
                  <code className={styles.prefix}>{ch.apiKey.keyPrefix}…</code>
                ) : (
                  <span className={styles.cellMuted}>No key</span>
                )}
              </div>
              <div className={styles.cell}>
                {(ch.apiKey?.usageCount || 0).toLocaleString()}
              </div>
              <div className={styles.cell}>
                <span className={`${styles.statusBadge} ${ch.active ? styles.active : styles.revoked}`}>
                  {ch.active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className={styles.cell} style={{ gap: '0.25rem' }}>
                <button className={styles.actionBtn} onClick={() => handleStartEdit(ch)} title="Edit channel">
                  <Edit2 size={14} />
                </button>
                <button className={styles.rotateBtn} onClick={() => setRotateId(ch.id)} title="Regenerate key">
                  <RefreshCw size={14} />
                </button>
                <button className={styles.revokeBtn} onClick={() => setDeleteId(ch.id)} title="Delete channel">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Create / Edit modal */}
      {showModal && (
        <div className={styles.overlay} onClick={e => { if (e.target === e.currentTarget) handleCloseModal(); }}>
          <div className={`${styles.modal} ${styles.modalWide}`}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>{editingChannel ? 'Edit Channel' : 'Create Channel'}</h2>
              <button className={styles.dismissBtn} onClick={handleCloseModal}><X size={16} /></button>
            </div>
            <div className={styles.modalBody}>
              {/* Name + Description */}
              <div className={styles.field}>
                <label className={styles.label}>Channel Name <span className={styles.req}>*</span></label>
                <input className={styles.input} placeholder="e.g. Customer Portal, Partner API" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} autoFocus />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Description</label>
                <input className={styles.input} placeholder="Where will this channel be used?" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              </div>

              {/* Channel Type */}
              <div className={styles.field}>
                <label className={styles.label}>Channel Type</label>
                <div className={styles.channelTypeGrid}>
                  {CHANNEL_TYPES.map(ct => (
                    <button
                      key={ct.value}
                      type="button"
                      className={`${styles.channelTypeCard} ${form.type === ct.value ? styles.channelTypeCardActive : ''}`}
                      onClick={() => setForm(f => ({ ...f, type: ct.value }))}
                    >
                      <span className={styles.channelTypeIcon}>{ct.icon}</span>
                      <span className={styles.channelTypeLabel}>{ct.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Auth Mode */}
              <div className={styles.field}>
                <label className={styles.label}>End-User Authentication</label>
                <div className={styles.authModeGrid}>
                  {AUTH_MODES.map(am => (
                    <button
                      key={am.value}
                      type="button"
                      className={`${styles.authModeCard} ${form.authMode === am.value ? styles.authModeCardActive : ''}`}
                      onClick={() => setForm(f => ({ ...f, authMode: am.value }))}
                    >
                      <am.Icon size={18} className={styles.authModeIcon} />
                      <div className={styles.authModeCardContent}>
                        <strong>{am.label}</strong>
                        <span className={styles.authModeDesc}>{am.desc}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* JWT Config — shown when AUTHENTICATED */}
              {(form.authMode === 'AUTHENTICATED' || form.authMode === 'OPTIONAL') && (
                <div className={styles.jwtConfig}>
                  <div className={styles.jwtConfigTitle}>🔑 JWT Verification Config</div>
                  <div className={styles.fieldHint} style={{ marginBottom: '0.5rem', lineHeight: '1.4' }}>
                    <strong>Identity Providers:</strong>
                    <ul style={{ paddingLeft: '1.25rem', marginTop: '0.25rem' }}>
                      <li>
                        <strong>Google Accounts:</strong> JWKS: <code>https://www.googleapis.com/oauth2/v3/certs</code> | Issuer: <code>https://accounts.google.com</code>
                      </li>
                      <li>
                        <strong>Microsoft Entra / Office 365:</strong> JWKS: <code>https://login.microsoftonline.com/common/discovery/v2.0/keys</code> | Issuer: <code>https://login.microsoftonline.com/{"<tenant-id>"}/v2.0</code>
                      </li>
                      <li style={{ marginTop: '0.375rem', listStyleType: 'none', marginLeft: '-1.25rem' }}>
                        <details style={{ cursor: 'pointer' }}>
                          <summary style={{ color: 'var(--color-primary)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            🛠️ Custom / Own Auth System (Click to expand)
                          </summary>
                          <div style={{ marginTop: '0.375rem', paddingLeft: '0.75rem', borderLeft: '2px solid var(--color-primary)', display: 'flex', flexDirection: 'column', gap: '0.375rem', cursor: 'default', color: 'var(--color-text-muted)' }}>
                            <span>1. Expose your public RSA keys in JWKS format at a JSON endpoint (e.g. <code>https://yoursite.com/.well-known/jwks.json</code>).</span>
                            <span>2. Set <strong>JWKS URL</strong> to that endpoint, and configure your own custom <strong>Issuer</strong> and <strong>Audience</strong> tags.</span>
                            <span>3. When users log in, sign RS256 JWT tokens with your private key on your backend, and pass the generated token string to the Chat SDK initialization.</span>
                          </div>
                        </details>
                      </li>
                    </ul>
                  </div>
                  <div className={styles.field}>
                    <label className={styles.label}>JWKS URL {form.authMode === 'AUTHENTICATED' && <span className={styles.req}>*</span>}</label>
                    <input className={styles.input} placeholder="https://your-idp.com/.well-known/jwks.json" value={form.jwksUrl} onChange={e => setForm(f => ({ ...f, jwksUrl: e.target.value }))} />
                    <span className={styles.fieldHint}>JSON Web Key Set endpoint for signature verification</span>
                  </div>
                  <div className={styles.formGrid}>
                    <div className={styles.field}>
                      <label className={styles.label}>Issuer</label>
                      <input className={styles.input} placeholder="https://your-idp.com/" value={form.jwtIssuer} onChange={e => setForm(f => ({ ...f, jwtIssuer: e.target.value }))} />
                    </div>
                    <div className={styles.field}>
                      <label className={styles.label}>Audience</label>
                      <input className={styles.input} placeholder="your-app-client-id" value={form.jwtAudience} onChange={e => setForm(f => ({ ...f, jwtAudience: e.target.value }))} />
                    </div>
                  </div>
                </div>
              )}

              {/* Routing + Source App */}
              <div className={styles.formGrid}>
                <div className={styles.field}>
                  <label className={styles.label}>Source App Tag</label>
                  <input className={styles.input} placeholder="auto-generated from name" value={form.sourceApp} onChange={e => setForm(f => ({ ...f, sourceApp: e.target.value }))} />
                  <span className={styles.fieldHint}>URL-safe slug to tag conversations</span>
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Route to Agent</label>
                  <select className={styles.input} value={form.agentId} onChange={e => setForm(f => ({ ...f, agentId: e.target.value }))}>
                    <option value="">Platform routing (auto)</option>
                    {agents.filter(a => a.status === 'ACTIVE').map(a => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Default Session Context */}
              <div className={styles.field}>
                <label className={styles.label}>Default Session Context</label>
                <textarea
                  className={`${styles.input} ${styles.textarea}`}
                  placeholder={'{\n  "tier": "premium",\n  "region": "EU"\n}'}
                  value={form.defaultSessionContext}
                  onChange={e => setForm(f => ({ ...f, defaultSessionContext: e.target.value }))}
                  rows={3}
                />
                <span className={styles.fieldHint}>JSON object merged into every conversation from this channel</span>
              </div>

              {/* Origins + Rate Limit */}
              <div className={styles.field}>
                <label className={styles.label}>Allowed Origins</label>
                <input className={styles.input} placeholder="https://mysite.com, https://staging.mysite.com" value={form.allowedOrigins} onChange={e => setForm(f => ({ ...f, allowedOrigins: e.target.value }))} />
                <span className={styles.fieldHint}>Comma-separated. Leave empty to allow all origins.</span>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Rate Limit (req/min)</label>
                <input type="number" className={styles.input} value={form.rateLimitPerMinute} onChange={e => setForm(f => ({ ...f, rateLimitPerMinute: Number(e.target.value) }))} min={1} max={1000} />
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={handleCloseModal}>Cancel</button>
              <button
                id="confirm-create-channel"
                className={styles.confirmBtn}
                onClick={handleSave}
                disabled={!form.name.trim() || (form.authMode === 'AUTHENTICATED' && !form.jwksUrl.trim()) || saving}
              >
                {saving ? (editingChannel ? 'Saving…' : 'Creating…') : (editingChannel ? 'Save Changes' : 'Create Channel & Key')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteId}
        onCancel={() => setDeleteId(null)}
        onConfirm={() => deleteId && handleDelete(deleteId)}
        title="Delete Channel"
        description="This will delete the channel and revoke its key. All integrations using this key will immediately stop working."
        confirmLabel="Delete Channel"
        variant="danger"
      />

      <ConfirmDialog
        open={!!rotateId}
        onCancel={() => setRotateId(null)}
        onConfirm={handleRotateKey}
        title="Regenerate Channel Key"
        description="This will generate a new API key for this channel. The old key will be revoked immediately and any integrations using it will fail. Do you want to continue?"
        confirmLabel="Regenerate Key"
        variant="warning"
        loading={rotating}
      />
    </div>
  );
}
